/**
 * SendNowModal
 *
 * Premium (tier 3) exclusive. Lets the user send a card to any of their
 * contacts immediately — no occasion or schedule needed.
 *
 * 3-step flow:
 *   1. Pick a contact
 *   2. Write your message
 *   3. Confirm & send
 */

import { useState } from 'react';
import { apiFetch } from '../../lib/api';
import type { Contact } from '../../../../shared/src/types/database';

interface SendNowModalProps {
  contacts: Contact[];
  onClose:  () => void;
  onSent?:  () => void;
  /** Included folded cards the user has left this period. 0 → this send bills $5.99. */
  allowanceBalance?: number;
}

const MAX_CHARS = 650;
const OVERAGE_PRICE = '$5.99';

type Step = 'pick' | 'write' | 'confirm' | 'sent';

export default function SendNowModal({ contacts, onClose, onSent, allowanceBalance = 0 }: SendNowModalProps) {
  const [step,        setStep       ] = useState<Step>('pick');
  const [selected,    setSelected   ] = useState<Contact | null>(null);
  const [message,     setMessage    ] = useState('');
  const [sending,     setSending    ] = useState(false);
  const [error,       setError      ] = useState('');
  const [search,      setSearch     ] = useState('');

  const filtered = contacts.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const charCount   = message.length;
  const overLimit   = charCount > MAX_CHARS;

  async function handleSend() {
    if (!selected || !message.trim()) return;
    setSending(true);
    setError('');
    try {
      await apiFetch('/api/cards/send-now', {
        method: 'POST',
        body: JSON.stringify({
          contactId:   selected.id,
          message:     message.trim(),
        }),
      });
      setStep('sent');
      onSent?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md flex flex-col overflow-hidden">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="font-heading text-lg font-semibold text-navy">Send a card now</h2>
            <p className="font-body text-xs text-gray-400 mt-0.5">
              Premium feature · no occasion needed
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Step indicators ─────────────────────────────────────────────── */}
        {step !== 'sent' && (
          <div className="flex items-center gap-2 px-6 pt-4">
            {(['pick', 'write', 'confirm'] as const).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold font-body transition-colors ${
                    step === s
                      ? 'bg-navy text-white'
                      : (i < (['pick', 'write', 'confirm'] as const).indexOf(step))
                        ? 'bg-sage text-navy'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {i + 1}
                </div>
                {i < 2 && <div className="h-px w-6 bg-gray-200" />}
              </div>
            ))}
            <span className="ml-2 font-body text-xs text-gray-400 capitalize">{step === 'pick' ? 'Choose contact' : step === 'write' ? 'Write message' : 'Review & send'}</span>
          </div>
        )}

        {/* ── Step 1: Pick contact ─────────────────────────────────────────── */}
        {step === 'pick' && (
          <div className="flex flex-col gap-3 px-6 py-5">
            <input
              type="text"
              placeholder="Search contacts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 font-body text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
            />
            <div className="max-h-64 overflow-y-auto flex flex-col gap-1">
              {filtered.length === 0 && (
                <p className="font-body text-sm text-gray-400 text-center py-6">No contacts found.</p>
              )}
              {filtered.map((c) => {
                const hasAddress = !!(c.address_street && c.address_city);
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={!hasAddress}
                    onClick={() => { setSelected(c); setStep('write'); }}
                    className={`flex items-center justify-between rounded-xl px-4 py-3 text-left transition-colors ${
                      hasAddress
                        ? 'hover:bg-sage/10 cursor-pointer'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div>
                      <p className="font-body text-sm font-semibold text-navy">{c.name}</p>
                      {!hasAddress && (
                        <p className="font-body text-xs text-amber-600">No address on file</p>
                      )}
                    </div>
                    {hasAddress && (
                      <svg className="h-4 w-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Step 2: Write message ────────────────────────────────────────── */}
        {step === 'write' && selected && (
          <div className="flex flex-col gap-4 px-6 py-5">
            <p className="font-body text-sm text-gray-500">
              Writing to <span className="font-semibold text-navy">{selected.name}</span>
            </p>

            {/* Message textarea */}
            <div>
              <textarea
                rows={5}
                placeholder={`Write your message to ${selected.name}…`}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 font-body text-sm text-navy placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
              />
              <p className={`font-body text-xs mt-1 text-right ${overLimit ? 'text-rose-500' : 'text-gray-400'}`}>
                {charCount}/{MAX_CHARS}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep('pick')}
                className="flex-1 rounded-xl border border-gray-200 py-3 font-body text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <button
                type="button"
                disabled={message.trim().length < 5 || overLimit}
                onClick={() => setStep('confirm')}
                className="flex-1 rounded-xl bg-navy text-white py-3 font-body text-sm font-semibold hover:bg-navy/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Review →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Confirm & send ───────────────────────────────────────── */}
        {step === 'confirm' && selected && (
          <div className="flex flex-col gap-4 px-6 py-5">
            <div className="rounded-xl bg-sage/10 border border-sage/20 px-4 py-3">
              <p className="font-body text-xs font-semibold text-sage uppercase tracking-wide mb-2">Sending to</p>
              <p className="font-body text-sm font-semibold text-navy">{selected.name}</p>
              {selected.address_city && (
                <p className="font-body text-xs text-gray-400">{selected.address_city}{selected.address_state ? `, ${selected.address_state}` : ''}</p>
              )}
            </div>

            <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
              <p className="font-body text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Your message</p>
              <p className="font-body text-sm text-navy whitespace-pre-wrap leading-relaxed">{message.trim()}</p>
            </div>

            {/* Allowance / overage notice — so a billable send is never a surprise */}
            {allowanceBalance > 0 ? (
              <div className="flex items-start gap-2.5 rounded-xl bg-sage/10 border border-sage/20 px-4 py-3">
                <span className="text-base leading-none mt-0.5">✓</span>
                <p className="font-body text-xs text-gray-600 leading-relaxed">
                  This uses <strong className="text-navy">1 of your {allowanceBalance} included {allowanceBalance === 1 ? 'card' : 'cards'}</strong> left this period. No extra charge.
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                <span className="text-base leading-none mt-0.5">💳</span>
                <p className="font-body text-xs text-amber-800 leading-relaxed">
                  Heads up: you've used your included cards for this period, so this card will be billed at the flat <strong>{OVERAGE_PRICE} overage</strong> rate, added to your next invoice.
                </p>
              </div>
            )}

            {error && (
              <p className="font-body text-xs text-rose-600 text-center">{error}</p>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep('write')}
                className="flex-1 rounded-xl border border-gray-200 py-3 font-body text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                ← Edit
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={handleSend}
                className="flex-1 rounded-xl bg-sage text-navy py-3 font-body text-sm font-semibold hover:bg-sage/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? 'Sending…' : (allowanceBalance > 0 ? 'Send card ✉️' : `Send and pay ${OVERAGE_PRICE} ✉️`)}
              </button>
            </div>
          </div>
        )}

        {/* ── Sent confirmation ────────────────────────────────────────────── */}
        {step === 'sent' && selected && (
          <div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
            <div className="text-5xl">✉️</div>
            <h3 className="font-heading text-xl font-semibold text-navy">Card sent!</h3>
            <p className="font-body text-sm text-gray-500 max-w-xs">
              Your card to <span className="font-semibold text-navy">{selected.name}</span> has
              been submitted to print and will be mailed shortly.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 rounded-xl bg-navy text-white px-8 py-3 font-body text-sm font-semibold hover:bg-navy/90 transition-colors"
            >
              Done
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
